import React, { useContext, useEffect } from 'react';
import { connect, DispatchProp } from 'react-redux';
import { CButton, CForm, CModal, CModalBody, CModalFooter, CModalHeader } from '@coreui/react';
import * as Yup from 'yup';
import { FormikProvider, useFormik } from 'formik';
import { isValidPhoneNumber } from 'react-phone-number-input/input';
import { compact, isEmpty } from 'lodash';
import LoadingButton from '../../../shared/components/LoadingButton';
import { Job, JobPayload } from '../../../shared/models/jobs';
import { UserInfo } from '../../../shared/models/user';
import { RootState } from '../../../store/state';
import { selectTechnicians } from '../../../store/selectors/user.selectors';
import JobForm, { JobFormMode } from '../../components/JobForm';
import { ResetUpdateJobState, UpdateJob } from '../../actions';
import { ConfigContext } from '../../../shared/contexts/journeyConfig.context';
import phoneUnification from '../../../shared/utils/validation-utils';
import { getEmails, getPhones, getRecipients } from '../../../shared/utils/job-utils';

export const mapData = (data) => {
  return {
    description: data.description,
    destination: {
      name: data.destination.name,
      address: data.destination.address,
      lat: data.destination.lat,
      lng: data.destination.lng,
      showCoordinates: !data.destination.address,
    },
    appointment: {
      from: new Date(data?.metadata?.find(meta => meta.n === 'appt_from')?.v ?? 0),
      to: new Date(data?.metadata?.find(meta => meta.n === 'appt_to')?.v ?? 0),
      id: data?.metadata?.find(meta => meta.n === 'appointment_id')?.v,
    },
    customer: {
      name: data?.metadata?.find(meta => meta.n === 'era_recipient_name')?.v,
      phones: getPhones(data?.recipients),
      emails: getEmails(data?.recipients),
    },
    technician: data.agent_id,
  } as JobPayload;
};

const validationSchema = Yup.object({
  appointment: Yup.object({
    from: Yup.date().required('Arrival Window Start is required'),
    to: Yup.date().nullable().default(null)
      .when("from",
        (from, yup) => {
          const time = from?.getTime();
          return (time && !Number.isNaN(time)) ? yup.min(from, "Arrival Window End date cannot be before Arrival Window Start date") : yup;
        }),
    id: Yup.string(),
  }),
  description: Yup.string().required('Description is required'),
  customer: Yup.object().shape({
    name: Yup.string().required('Name is required'),
    phones: Yup.array().of(
      Yup.string().test('phone-validation', 'Please enter a valid US or Canada number', (value) => {
        const phone = phoneUnification(value);
        return !phone || isValidPhoneNumber(phone);
      })
    ).when(['emails'], {
      is: emails => !emails || !compact(emails).length,
      then: Yup.array().of(
        Yup.string().test('phone-validation', 'Please enter a valid US or Canada number', (value) => {
          const phone = phoneUnification(value);
          return phone && isValidPhoneNumber(phone);
        })
      ).min(1, 'At least one phone number is required')
    }),
    emails: Yup.array().of(
      Yup.string().test('email-validation', 'Email is not valid', (value) => {
        return !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      })
    ).when(['phones'], {
      is: phones => !phones || !compact(phones).length,
      then: Yup.array().of(
        Yup.string().test('email-validation', 'Email is not valid', (value) => {
          return value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        })
      ).min(1, 'At least one email is required')
    }),
  }, [['phones', 'emails']]),
  technician: Yup.number().required('Technician is required'),
  destination: Yup.object().shape({
    // eslint-disable-next-line react/forbid-prop-types
    lat: Yup.number().when(['address'], {
      is: address => {
        return !address;
      },
      then: Yup.number().required('Lat is required'),
    }),
    lng: Yup.number().when(['address'], {
      is: address => {
        return !address;
      },
      then: Yup.number().required('Lng is required'),
    }),
    address: Yup.string().when(['name'], {
      is: (name) => {
        return !name;
      },
      then: Yup.string().required('Address is required'),
    }),
    name: Yup.string().when(['address'], {
      is: address => {
        return !address;
      },
      then: Yup.string().required('Destination Name is required'),
    }),
    showCoordinates: Yup.bool(),
  }, [['lat', 'address'], ['lng', 'address'], ['address', 'name']])
});

export interface EditJobProps extends DispatchProp {
  show: boolean;
  close: () => void;
  job: Job;
  technicians: UserInfo[];
  saving: boolean;
  saved: boolean;
}

const EditJobModal = (props: EditJobProps) => {
  const { show, close, job, technicians, dispatch, saved, saving } = props;
  const labels = useContext(ConfigContext);

  useEffect(() => {
    if (saved) {
      dispatch(new ResetUpdateJobState());
      close();
    }
  }, [saved]);

  const onSubmit = (values: JobPayload) => {
    const recipients = getRecipients(job?.recipients, [...values.customer.phones, ...values.customer.emails]);
    
    const payload = {
      ...values,
      recipients
    };
    
    dispatch(new UpdateJob(job._id, payload));
  };

  const formik = useFormik({
      initialValues: mapData(job),
      validationSchema,
      onSubmit,
    },
  );

  React.useEffect(() => {
    if (!isEmpty(job)) {
      formik.resetForm({ values: mapData(job) });
    }
  }, [job]);

  return (
    show && <CModal className="mt-5 pt-5" id="edit-job-modal" show={ show } onClose={ close } closeOnBackdrop={ false }>
      <CModalHeader closeButton>Edit {labels.task}</CModalHeader>
      <CForm onSubmit={ formik.handleSubmit }>
        <FormikProvider value={formik}>
          <CModalBody>
            <JobForm isEdit formik={ formik } technicians={ technicians } mode={ !job.start_time ? JobFormMode.Edit : JobFormMode.EditStartedJob }/>
          </CModalBody>
          <CModalFooter className="glympse-footer">
          <div className="glympse-btnWrapper">
            <LoadingButton color='info' type='submit' className="pl-5 pr-5 bg-color-orange pl-5 pr-5 border-0 roundedBtn roundBottomShadow" isLoading={ saving }>Save {labels.task}</LoadingButton>
          </div>
          <div className="glympse-btnWrapper">
            <CButton className="glympse-cancelBtn" color="light" shape="rounded-pill" variant="ghost" onClick={ close }>Cancel</CButton>
          </div>
          </CModalFooter>
        </FormikProvider>
      </CForm>
    </CModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  technicians: selectTechnicians(state),
  saving: state?.jobs?.updateJob?.saving,
  saved: state?.jobs?.updateJob?.saved,
});

export default connect(mapStateToProps)(EditJobModal);
